import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, FeeVersionInput } from '../api';
import { eventEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table } from '../ui';
import { INPUT, dollars, humanize, toCents } from './event/shared';

/**
 * Fee rules and tax rules: shared configuration events attach. Both are
 * versioned; creating a rule makes a draft version that has to be activated
 * before any event feels it, and changing one later is a new version.
 */

const FEE_TYPES = ['FACILITY_FEE', 'ONLINE_CONVENIENCE_FEE', 'ORDER_FEE', 'PROMOTER_FEE', 'PROCESSING_FEE', 'DELIVERY_FEE', 'CUSTOM_FEE'];
const CODE_FOR_TYPE: Record<string, string> = {
  FACILITY_FEE: 'FACILITY_FEE',
  ONLINE_CONVENIENCE_FEE: 'ONLINE_CONVENIENCE_FEE',
  ORDER_FEE: 'ORDER_FEE',
  PROMOTER_FEE: 'PROMOTER_FEE',
  PROCESSING_FEE: 'PROCESSING_FEE_CHARGED',
  DELIVERY_FEE: 'DELIVERY_FEE',
  CUSTOM_FEE: 'CUSTOM_FEE',
};
const CHANNELS = ['WEB', 'BOX_OFFICE', 'PHONE', 'GROUP_SALES'];

export default function FeeRulesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [addingFee, setAddingFee] = useState(false);
  const [addingTax, setAddingTax] = useState(false);
  const fees = useQuery({ queryKey: ['fee-rules'], queryFn: eventEndpoints.feeRules });
  const taxes = useQuery({ queryKey: ['tax-rules'], queryFn: eventEndpoints.taxRules });
  const onError = (cause: ApiError) => setError(cause.message);
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['fee-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['tax-rules'] });
  };

  const toggleFee = useMutation({ mutationFn: ({ id, active }: { id: string; active: boolean }) => eventEndpoints.setFeeRuleActive(id, active), onSuccess: refresh, onError });
  const activateDraft = useMutation({
    mutationFn: async (ruleId: string) => {
      const rule = await eventEndpoints.feeRule(ruleId);
      const draft = rule.versions.find((v) => v.status === 'DRAFT') as { id: string } | undefined;
      if (!draft) throw new Error('No draft version');
      return eventEndpoints.activateFeeVersion(ruleId, draft.id);
    },
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });
  const activateTaxDraft = useMutation({
    mutationFn: async (ruleId: string) => {
      const rule = await eventEndpoints.taxRule(ruleId);
      const draft = rule.versions.find((v) => v.status === 'DRAFT');
      if (!draft) throw new Error('No draft version');
      return eventEndpoints.activateTaxVersion(ruleId, draft.id);
    },
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  });

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  const canFees = can('fees.manage');
  const canTaxes = can('taxes.manage');

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {addingFee && canFees ? <NewFeeRule onDone={() => { setAddingFee(false); refresh(); }} onCancel={() => setAddingFee(false)} onError={onError} /> : null}
      <Panel
        title="Fee rules"
        description="Shared across events. An event feels a rule only once it is attached there, and only through the rule's active version."
        actions={canFees && !addingFee ? <Button variant="primary" onClick={() => setAddingFee(true)}>New fee rule</Button> : null}
      >
        <Table headers={['Rule', 'Type', 'Active version', 'Applies', 'Refundable', 'Status', '']}>
          {(fees.data?.fee_rules ?? []).length === 0 ? (
            <EmptyRow columns={7}>No fee rules yet. Typical first ones: a facility fee per ticket and a web-only convenience fee.</EmptyRow>
          ) : (
            fees.data!.fee_rules.map((f) => (
              <tr key={f.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{f.name ?? f.code}</div>
                  <div className="text-xs text-ink-400">{f.code}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(f.fee_type)}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {f.active_version
                    ? `v${f.active_version}: ${f.calculation_method === 'PERCENT' ? `${(f.basis_points ?? 0) / 100}%` : f.calculation_method === 'PRICE_BANDS' ? 'by band' : dollars(f.amount_cents)}${f.calculation_method === 'FIXED_PLUS_PERCENT' ? ` + ${(f.basis_points ?? 0) / 100}%` : ''}${f.included_in_price ? ' (in price)' : ''}`
                    : 'none'}
                  {f.drafts > 0 ? <span className="ml-1 text-xs text-accent">+ draft</span> : null}
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{humanize(f.application)}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{f.refundable === null ? '—' : f.refundable ? 'yes' : 'no'}</td>
                <td className="px-5 py-3">
                  <Badge tone={f.active ? 'ok' : 'neutral'}>{f.active ? 'ENABLED' : 'DISABLED'}</Badge>
                </td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  {canFees && f.drafts > 0 ? (
                    <Button variant="primary" onClick={() => activateDraft.mutate(f.id)} disabled={activateDraft.isPending}>
                      Activate draft
                    </Button>
                  ) : null}
                  {canFees ? (
                    <Button className="ml-2" onClick={() => toggleFee.mutate({ id: f.id, active: !f.active })}>
                      {f.active ? 'Disable' : 'Enable'}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {addingTax && canTaxes ? <NewTaxRule onDone={() => { setAddingTax(false); refresh(); }} onCancel={() => setAddingTax(false)} onError={onError} /> : null}
      <Panel
        title="Tax rules"
        description="Rates in percent; stored as parts per million. Which taxes apply to admissions and fees is open question Q-03."
        actions={canTaxes && !addingTax ? <Button variant="primary" onClick={() => setAddingTax(true)}>New tax rule</Button> : null}
      >
        <Table headers={['Rule', 'Jurisdiction', 'Active version', '']}>
          {(taxes.data?.tax_rules ?? []).length === 0 ? (
            <EmptyRow columns={4}>No tax rules yet.</EmptyRow>
          ) : (
            taxes.data!.tax_rules.map((t) => (
              <tr key={t.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <div className="font-medium text-ink-900">{t.name ?? t.code}</div>
                  <div className="text-xs text-ink-400">{t.code}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{t.jurisdiction}</td>
                <td className="tabular px-5 py-3 text-sm text-ink-600">
                  {t.active_version ? `v${t.active_version}: ${(t.rate_ppm ?? 0) / 10000}%` : 'none'}
                  {t.drafts > 0 ? <span className="ml-1 text-xs text-accent">+ draft</span> : null}
                </td>
                <td className="px-5 py-3 text-right">
                  {canTaxes && t.drafts > 0 ? (
                    <Button variant="primary" onClick={() => activateTaxDraft.mutate(t.id)} disabled={activateTaxDraft.isPending}>
                      Activate draft
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function NewFeeRule({ onDone, onCancel, onError }: { onDone: () => void; onCancel: () => void; onError: (e: ApiError) => void }) {
  const [code, setCode] = useState('FACILITY');
  const [type, setType] = useState('FACILITY_FEE');
  const [name, setName] = useState('Facility fee');
  const [method, setMethod] = useState('FIXED_CENTS');
  const [application, setApplication] = useState('PER_TICKET');
  const [basis, setBasis] = useState('FACE_VALUE');
  const [amount, setAmount] = useState('3.00');
  const [percent, setPercent] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [included, setIncluded] = useState(false);
  const [refundable, setRefundable] = useState(false);
  const [taxable, setTaxable] = useState(false);
  const [channels, setChannels] = useState<string[]>([]);
  const [activateNow, setActivateNow] = useState(true);

  const create = useMutation({
    mutationFn: async () => {
      const version: FeeVersionInput = {
        name: name.trim(),
        calculation_method: method,
        application,
        price_basis: application === 'PER_ORDER' ? 'ORDER_SUBTOTAL' : basis,
        amount_cents: method === 'FIXED_CENTS' || method === 'FIXED_PLUS_PERCENT' ? toCents(amount) : null,
        basis_points: method === 'PERCENT' || method === 'FIXED_PLUS_PERCENT' ? Math.round(Number(percent || 0) * 100) : null,
        min_cents: toCents(min),
        max_cents: toCents(max),
        included_in_price: included,
        refundable,
        taxable,
        accounting_code: CODE_FOR_TYPE[type] ?? 'CUSTOM_FEE',
        channels,
      };
      const created = await eventEndpoints.createFeeRule({ code: code.trim().toUpperCase(), fee_type: type, version });
      if (activateNow) {
        const rule = await eventEndpoints.feeRule(created.fee_rule_id);
        const draft = rule.versions.find((v) => v.status === 'DRAFT') as { id: string } | undefined;
        if (draft) await eventEndpoints.activateFeeVersion(created.fee_rule_id, draft.id);
      }
    },
    onSuccess: onDone,
    onError,
  });

  return (
    <Panel title="New fee rule" description="Creates the rule and its first version. Attach it to events from each event's Fees & taxes tab.">
      <form
        className="space-y-3 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Code" htmlFor="fr-code">
            <input id="fr-code" value={code} onChange={(e) => setCode(e.target.value)} className={`w-40 ${INPUT}`} />
          </Field>
          <Field label="Name" htmlFor="fr-name">
            <input id="fr-name" value={name} onChange={(e) => setName(e.target.value)} className={`w-56 ${INPUT}`} />
          </Field>
          <Field label="Fee type" htmlFor="fr-type">
            <select id="fr-type" value={type} onChange={(e) => setType(e.target.value)} className={INPUT}>
              {FEE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {humanize(t)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Applied" htmlFor="fr-application">
            <select id="fr-application" value={application} onChange={(e) => setApplication(e.target.value)} className={INPUT}>
              <option value="PER_TICKET">per ticket</option>
              <option value="PER_ORDER">per order</option>
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Method" htmlFor="fr-method">
            <select id="fr-method" value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
              <option value="FIXED_CENTS">fixed amount</option>
              <option value="PERCENT">percentage</option>
              <option value="FIXED_PLUS_PERCENT">fixed + percentage</option>
            </select>
          </Field>
          {method !== 'PERCENT' ? (
            <Field label="Amount $" htmlFor="fr-amount">
              <input id="fr-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={`tabular w-24 ${INPUT}`} />
            </Field>
          ) : null}
          {method !== 'FIXED_CENTS' ? (
            <Field label="Percent" htmlFor="fr-percent">
              <input id="fr-percent" value={percent} onChange={(e) => setPercent(e.target.value)} inputMode="decimal" className={`tabular w-20 ${INPUT}`} />
            </Field>
          ) : null}
          {application === 'PER_TICKET' ? (
            <Field label="Of" htmlFor="fr-basis">
              <select id="fr-basis" value={basis} onChange={(e) => setBasis(e.target.value)} className={INPUT}>
                <option value="FACE_VALUE">face value</option>
                <option value="SELLING_PRICE">selling price</option>
              </select>
            </Field>
          ) : null}
          <Field label="Min $" htmlFor="fr-min">
            <input id="fr-min" value={min} onChange={(e) => setMin(e.target.value)} inputMode="decimal" className={`tabular w-20 ${INPUT}`} />
          </Field>
          <Field label="Max $" htmlFor="fr-max">
            <input id="fr-max" value={max} onChange={(e) => setMax(e.target.value)} inputMode="decimal" className={`tabular w-20 ${INPUT}`} />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={refundable} onChange={(e) => setRefundable(e.target.checked)} /> refundable
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} /> taxable
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={included} onChange={(e) => setIncluded(e.target.checked)} disabled={application === 'PER_ORDER'} /> included in the ticket price
          </label>
          <span className="text-ink-400">Channels (none = all):</span>
          {CHANNELS.map((ch) => (
            <label key={ch} className="flex items-center gap-1">
              <input type="checkbox" checked={channels.includes(ch)} onChange={(e) => setChannels(e.target.checked ? [...channels, ch] : channels.filter((x) => x !== ch))} /> {humanize(ch)}
            </label>
          ))}
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)} /> activate immediately
          </label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create fee rule'}
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function NewTaxRule({ onDone, onCancel, onError }: { onDone: () => void; onCancel: () => void; onError: (e: ApiError) => void }) {
  const [code, setCode] = useState('IA-SALES');
  const [jurisdiction, setJurisdiction] = useState('Iowa');
  const [name, setName] = useState('Iowa sales tax');
  const [rate, setRate] = useState('7');
  const [onFace, setOnFace] = useState(true);
  const [onFees, setOnFees] = useState(true);
  const [activateNow, setActivateNow] = useState(true);
  const create = useMutation({
    mutationFn: async () => {
      const created = await eventEndpoints.createTaxRule({
          code: code.trim().toUpperCase(),
          jurisdiction: jurisdiction.trim(),
          version: { name: name.trim(), rate_ppm: Math.round(Number(rate || 0) * 10000), applies_to_face_value: onFace, applies_to_taxable_fees: onFees },
        });
      if (activateNow) await eventEndpoints.activateTaxVersion(created.tax_rule_id, created.version.id);
    },
    onSuccess: onDone,
    onError,
  });
  return (
    <Panel title="New tax rule">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Code" htmlFor="tr-code">
          <input id="tr-code" value={code} onChange={(e) => setCode(e.target.value)} className={`w-32 ${INPUT}`} />
        </Field>
        <Field label="Jurisdiction" htmlFor="tr-jur">
          <input id="tr-jur" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} className={`w-40 ${INPUT}`} />
        </Field>
        <Field label="Name" htmlFor="tr-name">
          <input id="tr-name" value={name} onChange={(e) => setName(e.target.value)} className={`w-56 ${INPUT}`} />
        </Field>
        <Field label="Rate %" htmlFor="tr-rate">
          <input id="tr-rate" value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" className={`tabular w-20 ${INPUT}`} />
        </Field>
        <label className="flex items-center gap-1 pb-1.5 text-sm">
          <input type="checkbox" checked={onFace} onChange={(e) => setOnFace(e.target.checked)} /> on face value
        </label>
        <label className="flex items-center gap-1 pb-1.5 text-sm">
          <input type="checkbox" checked={onFees} onChange={(e) => setOnFees(e.target.checked)} /> on taxable fees
        </label>
        <label className="flex items-center gap-1 pb-1.5 text-sm">
          <input type="checkbox" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)} /> activate immediately
        </label>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={!code.trim() || !name.trim() || create.isPending}>
            Create tax rule
          </Button>
          <Button type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
